import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

/*
 * Padrón de productores de la planilla (colección `productores`).
 *
 * Lo escribe únicamente la Cloud Function de sincronización con el Sistema de
 * Gestión Comercial (SGC): un documento por productor, con id = email en
 * minúsculas y la lista de números de comitente de sus clientes. Desde el
 * frontend solo se lee: las reglas de Firestore usan este mismo documento para
 * decidir qué puede ver cada usuario, así que nunca se edita desde la UI.
 */
const COLLECTION = 'productores';

const FUNCTIONS_BASE = 'https://us-central1-planilla-operaciones-axon.cloudfunctions.net';
const SYNC_FUNCTION_URL = `${FUNCTIONS_BASE}/syncProductoresPublic`;
const REFRESCAR_FUNCTION_URL = `${FUNCTIONS_BASE}/refrescarMisClientesPublic`;

export const normalizeProductorEmail = (email) => String(email || '').trim().toLowerCase();

const toProductor = (snap) => {
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    id: snap.id,
    ...data,
    comitentes: Array.isArray(data.comitentes) ? data.comitentes.map(String) : [],
    clientes: Array.isArray(data.clientes) ? data.clientes : [],
    clientesSinComitente: Array.isArray(data.clientesSinComitente) ? data.clientesSinComitente : [],
  };
};

/**
 * Suscribe al documento del productor con ese email. Emite `null` si el usuario
 * no figura en el padrón (o sea, es personal interno de Axon).
 */
export const subscribeProductorPorEmail = (email, onData, onError) => {
  const id = normalizeProductorEmail(email);
  if (!id) {
    onData(null);
    return () => {};
  }
  return onSnapshot(
    doc(db, COLLECTION, id),
    (snap) => onData(toProductor(snap)),
    (error) => {
      console.error('productoresService.subscribeProductorPorEmail failed:', { id, error });
      if (onError) onError(error);
    },
  );
};

/** Suscribe al padrón completo (solo para personal interno). */
export const subscribeProductores = (onData, onError) => onSnapshot(
  collection(db, COLLECTION),
  (snapshot) => onData(snapshot.docs.map(toProductor).filter(Boolean)),
  (error) => {
    console.error('productoresService.subscribeProductores failed:', error);
    if (onError) onError(error);
  },
);

async function postJson(url, user, body) {
  const idToken = await user.getIdToken();
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(body || {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.error || `HTTP ${response.status}`);
    error.code = data?.code || '';
    error.status = response.status;
    throw error;
  }
  return data;
}

/**
 * Pide al backend que vuelva a sincronizar el padrón contra el SGC ahora
 * (la sincronización programada corre sola cada hora en días hábiles).
 * Con `rebuildSaldos` además regenera los saldos por comitente del día.
 *
 * Con una caída anómala de personas activas, la respuesta trae `bajasOmitidas`
 * (se aplicaron altas y cambios, no las bajas); `forzar` repite la corrida
 * aplicándolas: es para cuando la baja masiva es legítima y alguien del personal
 * interno lo confirmó. Un error con code 'sync-anomalo' es un SGC vacío o ilegible
 * (o una versión anterior de la función, que abortaba también ante la caída).
 */
export const requestProductoresSync = async (user, { rebuildSaldos = false, forzar = false } = {}) => {
  try {
    return await postJson(SYNC_FUNCTION_URL, user, { rebuildSaldos, forzar });
  } catch (error) {
    console.error('productoresService.requestProductoresSync failed:', error);
    throw error;
  }
};

/**
 * El productor pide sincronizar su padrón contra el SGC ahora (para cuando el
 * cliente ya le fue asignado allá pero la corrida horaria todavía no pasó). El
 * padrón sale únicamente del SGC: desde la planilla no se agregan clientes.
 */
export const refrescarMisClientes = (user) => postJson(REFRESCAR_FUNCTION_URL, user, {});
